import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {customersApi, serversApi} from '../lib/api-resources';
import type {
  Customer,
  ListResponse,
  Server,
  ServerCheck,
  ServerCreate,
  ServerUpdate,
} from '../lib/types';
import {ApiError} from '../lib/api';
import Modal from '../components/Modal';
import Confirm from '../components/Confirm';

const OWNERSHIP_OPTIONS = [
  {value: '', labelKey: 'servers.filter.allOwnerships'},
  {value: 'SELF', labelKey: 'servers.filter.self'},
  {value: 'CUSTOMER', labelKey: 'servers.filter.customer'},
];

const MGMT_OPTIONS = [
  {value: '', labelKey: 'servers.filter.allModes'},
  {value: 'MANAGED', labelKey: 'servers.filter.managed'},
  {value: 'EXTERNAL', labelKey: 'servers.filter.external'},
];

function formatBytes(b: number | null): string {
  if (b == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = b;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatUptime(s: number | null): string {
  if (s == null) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusBadgeClass(status: string): string {
  if (status === 'ONLINE') return 'badge ok';
  if (status === 'OFFLINE') return 'badge err';
  if (status === 'ERROR') return 'badge warn';
  return 'badge muted';
}

export default function ServersPage() {
  const {t} = useTranslation();
  const [list, setList] = useState<ListResponse<Server> | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState<'' | 'SELF' | 'CUSTOMER'>('');
  const [mgmtFilter, setMgmtFilter] = useState<'' | 'MANAGED' | 'EXTERNAL'>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Server | null>(null);
  const [deleting, setDeleting] = useState<Server | null>(null);
  const [busy, setBusy] = useState(false);

  // Phase 2
  const [checking, setChecking] = useState<Server | null>(null);
  const [lastCheck, setLastCheck] = useState<ServerCheck | null>(null);
  const [detailServer, setDetailServer] = useState<Server | null>(null);
  const [detailHistory, setDetailHistory] = useState<ServerCheck[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [customers, setCustomers] = useState<Customer[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [serversRes, customersRes] = await Promise.all([
        serversApi.list({
          page,
          page_size: pageSize,
          search: search || undefined,
          ownership: ownershipFilter || undefined,
          management_mode: mgmtFilter || undefined,
        }),
        customersApi.list({page_size: 200}),
      ]);
      setList(serversRes);
      setCustomers(customersRes.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ownershipFilter, mgmtFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  async function handleSave(payload: ServerCreate | ServerUpdate) {
    setBusy(true);
    try {
      if (editing) {
        await serversApi.update(editing.id, payload as ServerUpdate);
      } else {
        await serversApi.create(payload as ServerCreate);
      }
      setCreating(false);
      setEditing(null);
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    setBusy(true);
    try {
      await serversApi.delete(deleting.id);
      setDeleting(null);
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleCheck(s: Server) {
    setChecking(s);
    setLastCheck(null);
    try {
      const result = await serversApi.check(s.id);
      setLastCheck(result);
      // Refresh list so server.status / last_check_at / versions are updated.
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      setError(msg);
    } finally {
      setChecking(null);
    }
  }

  async function openDetail(s: Server) {
    setDetailServer(s);
    setDetailHistory([]);
    setDetailLoading(true);
    try {
      const r = await serversApi.checks(s.id, {page: 1, page_size: 10});
      setDetailHistory(r.items);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load history';
      setError(msg);
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{t('servers.title')}</h1>
          <p className="page-subtitle">
            {list ? `${list.total} 个服务器` : t('common.loading')}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          {t('servers.add')}
        </button>
      </header>

      <div className="toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder={t('servers.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="btn btn-secondary">{t('common.search')}</button>
        </form>
        <select
          value={ownershipFilter}
          onChange={(e) => {
            setOwnershipFilter(e.target.value as '' | 'SELF' | 'CUSTOMER');
            setPage(1);
          }}
        >
          {OWNERSHIP_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
          ))}
        </select>
        <select
          value={mgmtFilter}
          onChange={(e) => {
            setMgmtFilter(e.target.value as '' | 'MANAGED' | 'EXTERNAL');
            setPage(1);
          }}
        >
          {MGMT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
          ))}
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('servers.table.name')}</th>
              <th>{t('servers.table.host')}</th>
              <th>{t('servers.table.ownership')}</th>
              <th>{t('servers.table.mgmt')}</th>
              <th>{t('servers.table.customer')}</th>
              <th>{t('servers.table.auth')}</th>
              <th>{t('servers.table.status')}</th>
              <th>{t('servers.table.lastCheck')}</th>
              <th className="num">{t('servers.table.sites')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="muted center">{t('common.loading')}</td>
              </tr>
            )}
            {!loading && list && list.items.length === 0 && (
              <tr>
                <td colSpan={10} className="muted center">{t('servers.empty')}</td>
              </tr>
            )}
            {list?.items.map((s) => (
              <tr key={s.id}>
                <td>
                  <div className="cell-strong">{s.name}</div>
                  <div className="cell-sub mono small">
                    {s.ssh_user}@{s.host}:{s.ssh_port}
                  </div>
                </td>
                <td className="mono small">{s.host}</td>
                <td>
                  <span className={`badge ${s.ownership === 'SELF' ? 'ok' : 'muted'}`}>
                    {s.ownership}
                  </span>
                </td>
                <td className="small">{s.management_mode}</td>
                <td>{s.customer_name || '—'}</td>
                <td>
                  <span className="small">
                    {s.auth_type}
                    {s.has_credential ? (
                      <span className="badge ok" style={{marginLeft: 6}}>●</span>
                    ) : (
                      <span className="badge err" style={{marginLeft: 6}}>○</span>
                    )}
                  </span>
                </td>
                <td>
                  <span className={statusBadgeClass(s.status)}>{s.status}</span>
                </td>
                <td className="small">
                  {s.last_check_at
                    ? new Date(s.last_check_at).toLocaleString()
                    : '—'}
                </td>
                <td className="num">{s.site_count}</td>
                <td className="actions">
                  <button
                    className="btn-link"
                    onClick={() => handleCheck(s)}
                    disabled={!s.has_credential || checking?.id === s.id}
                    title={s.has_credential ? t('servers.runCheckTitle') : t('servers.setCredsFirstTitle')}
                  >
                    {checking?.id === s.id ? t('servers.checking') : t('servers.check')}
                  </button>
                  <button className="btn-link" onClick={() => openDetail(s)}>
                    {t('common.details')}
                  </button>
                  <button className="btn-link" onClick={() => setEditing(s)}>
                    {t('common.edit')}
                  </button>
                  <button className="btn-link danger" onClick={() => setDeleting(s)}>
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {list && list.total_pages > 1 && (
        <div className="pager">
          <button
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← {t('common.previous')}
          </button>
          <span>
            {t('common.pageOf', {page, total: list.total_pages})}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= list.total_pages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common.next')} →
          </button>
        </div>
      )}

      {(creating || editing) && (
        <ServerFormModal
          server={editing}
          customers={customers}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={handleSave}
          busy={busy}
        />
      )}

      {deleting && (
        <Confirm
          open
          title={t('servers.deleteConfirm.title')}
          message={
            <>
              {t('servers.deleteConfirm.message', {name: deleting.name, host: deleting.host})}
              <br />
              <span className="muted small">
                {t('servers.deleteConfirm.hint')}
              </span>
            </>
          }
          confirmText={t('common.delete')}
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
          busy={busy}
        />
      )}

      {(checking || lastCheck) && (
        <CheckResultModal
          server={checking ?? lastCheck ? list?.items.find((s) => s.id === lastCheck?.server_id) ?? null : null}
          check={lastCheck}
          loading={!!checking}
          onClose={() => {
            setChecking(null);
            setLastCheck(null);
          }}
        />
      )}

      {detailServer && (
        <ServerDetailModal
          server={detailServer}
          history={detailHistory}
          loading={detailLoading}
          onClose={() => setDetailServer(null)}
          onCheckAgain={() => handleCheck(detailServer)}
        />
      )}
    </div>
  );
}

// ============================ Check result modal ============================
function CheckResultModal({
  server,
  check,
  loading,
  onClose,
}: {
  server: Server | null;
  check: ServerCheck | null;
  loading: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  return (
    <Modal
      open
      title={server ? t('servers.checkModal.titleWithName', {name: server.name}) : t('servers.checkModal.title')}
      onClose={onClose}
      size="lg"
      footer={
        <button className="btn btn-secondary" onClick={onClose}>
          {t('common.close')}
        </button>
      }
    >
      {loading && (
        <div className="muted center" style={{padding: '24px'}}>
          {t('servers.checkModal.connecting')}
        </div>
      )}
      {!loading && check && <CheckResultBody check={check} />}
    </Modal>
  );
}

function CheckResultBody({check}: {check: ServerCheck}) {
  return (
    <div className="check-result">
      <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16}}>
        <span className={statusBadgeClass(check.status)} style={{fontSize: 14, padding: '4px 12px'}}>
          {check.status}
        </span>
        <span className="muted small">
          {check.latency_ms} ms · SSH {check.ssh.ok ? `OK (${check.ssh.latency_ms}ms)` : 'FAILED'}
        </span>
      </div>

      {check.error_code && check.status !== 'ONLINE' && (
        <div className="alert alert-error" style={{marginBottom: 16}}>
          <strong>{check.error_code}</strong> — {check.error_message}
        </div>
      )}

      <div className="check-grid">
        <SystemCard system={check.system} />
        <ServicesCard services={check.services} />
      </div>
    </div>
  );
}

function SystemCard({system}: {system: ServerCheck['system']}) {
  return (
    <div className="card">
      <h3>System</h3>
      <dl>
        <dt>Hostname</dt><dd>{system.hostname || '—'}</dd>
        <dt>OS</dt><dd>{system.os || '—'}</dd>
        <dt>Kernel</dt><dd>{system.kernel || '—'}{system.arch ? ` (${system.arch})` : ''}</dd>
        <dt>Uptime</dt><dd>{formatUptime(system.uptime_seconds)}</dd>
        <dt>CPU</dt><dd>{system.cpu.cores != null ? `${system.cpu.cores} cores · ` : ''}{system.cpu.info || '—'}</dd>
        <dt>Memory</dt>
        <dd>
          {system.memory.total != null
            ? `${formatBytes(system.memory.available)} free / ${formatBytes(system.memory.total)} total`
            : '—'}
        </dd>
        <dt>Disk (/)</dt>
        <dd>
          {system.disk.total != null
            ? `${formatBytes(system.disk.free)} free / ${formatBytes(system.disk.total)} (${system.disk.percent ?? 0}%)`
            : '—'}
        </dd>
      </dl>
    </div>
  );
}

function ServicesCard({services}: {services: ServerCheck['services']}) {
  const ws = services.web_stack;
  const anyMysql = ws.mysql.installed ? ws.mysql : ws.mariadb.installed ? ws.mariadb : ws.mysql;
  const mysqlLabel = ws.mysql.installed
    ? `MySQL ${ws.mysql.version ?? '?'}`
    : ws.mariadb.installed
    ? `MariaDB ${ws.mariadb.version ?? '?'}`
    : 'Not installed';
  return (
    <div className="card">
      <h3>Services</h3>
      <dl>
        <dt>Nginx</dt>
        <dd>{ws.nginx.installed ? ws.nginx.version ?? 'installed' : <span className="muted">Not installed</span>}</dd>
        <dt>PHP</dt>
        <dd>{ws.php.installed ? ws.php.version ?? 'installed' : <span className="muted">Not installed</span>}</dd>
        <dt>MySQL / MariaDB</dt>
        <dd>{anyMysql.installed ? mysqlLabel : <span className="muted">Not installed</span>}</dd>
        <dt>WP-CLI</dt>
        <dd>{ws.wp_cli.installed ? ws.wp_cli.version ?? 'installed' : <span className="muted">Not installed</span>}</dd>

        <dt>curl / unzip / openssl</dt>
        <dd>
          <span className={services.dependencies.curl ? 'ok' : 'err'}>
            {services.dependencies.curl ? '✓' : '✗'} curl
          </span>{' '}
          <span className={services.dependencies.unzip ? 'ok' : 'err'}>
            {services.dependencies.unzip ? '✓' : '✗'} unzip
          </span>{' '}
          <span className={services.dependencies.openssl ? 'ok' : 'err'}>
            {services.dependencies.openssl ? '✓' : '✗'} openssl
          </span>
        </dd>

        <dt>Ports</dt>
        <dd>
          {services.ports.map((p) => (
            <span
              key={p.port}
              className="badge"
              style={{
                marginRight: 6,
                background:
                  p.state === 'LISTENING'
                    ? 'rgba(63,185,80,0.15)'
                    : p.state === 'NOT_LISTENING'
                    ? 'rgba(248,81,73,0.15)'
                    : undefined,
                color:
                  p.state === 'LISTENING'
                    ? 'var(--success)'
                    : p.state === 'NOT_LISTENING'
                    ? 'var(--error)'
                    : undefined,
              }}
              title={`${p.port} ${p.service ?? ''}: ${p.state}`}
            >
              {p.port}{p.service ? `/${p.service}` : ''} {p.state === 'LISTENING' ? '●' : p.state === 'NOT_LISTENING' ? '○' : '?'}
            </span>
          ))}
        </dd>

        <dt>User</dt>
        <dd>
          {services.permissions.user || '—'}
          {services.permissions.is_root === true ? ' (root)' : ''}
          {services.permissions.sudo_without_password === true
            ? ' · sudo ✓'
            : services.permissions.sudo_without_password === false
            ? ' · sudo ✗'
            : ''}
        </dd>

        {services.permissions.uid != null && (
          <>
            <dt>UID</dt>
            <dd className="mono small">{services.permissions.uid}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

// ============================ Server detail modal ============================
function ServerDetailModal({
  server,
  history,
  loading,
  onClose,
  onCheckAgain,
}: {
  server: Server;
  history: ServerCheck[];
  loading: boolean;
  onClose: () => void;
  onCheckAgain: () => void;
}) {
  const {t} = useTranslation();
  return (
    <Modal
      open
      title={`${server.name} (${server.host})`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onCheckAgain}>
            {t('servers.runCheckButton')}
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            {t('servers.detail.close')}
          </button>
        </>
      }
    >
      <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap'}}>
        <span className={statusBadgeClass(server.status)} style={{fontSize: 14, padding: '4px 12px'}}>
          {server.status}
        </span>
        <span className="muted small">
          {server.ssh_user}@{server.host}:{server.ssh_port} · {server.auth_type}
        </span>
        <span className="muted small">
          {t('servers.detail.lastCheck')} {server.last_check_at ? new Date(server.last_check_at).toLocaleString() : '—'}
        </span>
      </div>

      <div className="check-grid">
        <div className="card">
          <h3>{t('servers.detail.system')}</h3>
          <dl>
            <dt>{t('servers.detail.os')}</dt><dd>{server.os || '—'}</dd>
            <dt>{t('servers.detail.cpu')}</dt><dd>{server.cpu_info || '—'}</dd>
            <dt>{t('servers.detail.memory')}</dt><dd>{server.memory_total != null ? formatBytes(server.memory_total) : '—'}</dd>
            <dt>{t('servers.detail.disk')}</dt>
            <dd>
              {server.disk_total != null
                ? `${formatBytes(server.disk_free ?? 0)} free / ${formatBytes(server.disk_total)}`
                : '—'}
            </dd>
          </dl>
        </div>
        <div className="card">
          <h3>{t('servers.detail.software')}</h3>
          <dl>
            <dt>Nginx</dt><dd>{server.nginx_version || '—'}</dd>
            <dt>PHP</dt><dd>{server.php_version || '—'}</dd>
            <dt>MySQL / MariaDB</dt><dd>{server.mysql_version || '—'}</dd>
            <dt>WP-CLI</dt><dd>{server.wp_cli_version || '—'}</dd>
          </dl>
        </div>
      </div>

      <h3 style={{marginTop: 20, fontSize: 14, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.04em'}}>
        {t('servers.detail.checkHistory')}
      </h3>
      {loading && <div className="muted center" style={{padding: 16}}>{t('servers.detail.loading')}</div>}
      {!loading && history.length === 0 && (
        <div className="muted center" style={{padding: 16}}>{t('servers.detail.noChecks')}</div>
      )}
      {!loading && history.length > 0 && (
        <div className="table-wrap" style={{marginTop: 8}}>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('servers.detail.columnWhen')}</th>
                <th>{t('servers.detail.columnStatus')}</th>
                <th>{t('servers.detail.columnLatency')}</th>
                <th>{t('servers.detail.columnSsh')}</th>
                <th>{t('servers.detail.columnError')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td className="small">{formatDateTime(h.started_at)}</td>
                  <td>
                    <span className={statusBadgeClass(h.status)}>{h.status}</span>
                  </td>
                  <td className="num small">{h.latency_ms} ms</td>
                  <td className="small">{h.ssh.ok ? 'OK' : '—'}</td>
                  <td className="small">
                    {h.error_code ? (
                      <span title={h.error_message ?? ''}>{h.error_code}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ============================ Server form modal ============================
function ServerFormModal({
  server,
  customers,
  onClose,
  onSave,
  busy,
}: {
  server: Server | null;
  customers: Customer[];
  onClose: () => void;
  onSave: (p: ServerCreate | ServerUpdate) => Promise<void>;
  busy: boolean;
}) {
  const {t} = useTranslation();
  const [form, setForm] = useState({
    name: server?.name ?? '',
    ownership: (server?.ownership ?? 'SELF') as 'SELF' | 'CUSTOMER',
    management_mode: (server?.management_mode ?? 'MANAGED') as 'MANAGED' | 'EXTERNAL',
    customer_id: server?.customer_id ?? '',
    host: server?.host ?? '',
    ssh_port: server?.ssh_port ?? 22,
    ssh_user: server?.ssh_user ?? 'root',
    auth_type: (server?.auth_type ?? 'PASSWORD') as 'PASSWORD' | 'SSH_KEY',
    password: '',
    private_key: '',
  });

  function update<K extends keyof typeof form>(key: K, v: (typeof form)[K]) {
    setForm((f) => ({...f, [key]: v}));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (server) {
      const update: ServerUpdate = {
        name: form.name,
        ownership: form.ownership,
        management_mode: form.management_mode,
        host: form.host,
        ssh_port: form.ssh_port,
        ssh_user: form.ssh_user,
        auth_type: form.auth_type,
      };
      if (form.ownership === 'CUSTOMER') {
        update.customer_id = form.customer_id || null;
      } else {
        update.clear_customer = true;
      }
      if (form.auth_type === 'PASSWORD' && form.password) {
        update.password = form.password;
      } else if (form.auth_type === 'SSH_KEY' && form.private_key) {
        update.private_key = form.private_key;
      }
      await onSave(update);
    } else {
      const create: ServerCreate = {
        name: form.name,
        ownership: form.ownership,
        management_mode: form.management_mode,
        host: form.host,
        ssh_port: form.ssh_port,
        ssh_user: form.ssh_user,
        auth_type: form.auth_type,
      };
      if (form.ownership === 'CUSTOMER') {
        create.customer_id = form.customer_id || undefined;
      }
      if (form.auth_type === 'PASSWORD') {
        create.password = form.password;
      } else {
        create.private_key = form.private_key;
      }
      await onSave(create);
    }
  }

  return (
    <Modal
      open
      title={server ? t('servers.form.editTitle', {name: server.name}) : t('servers.form.newTitle')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t('servers.form.cancel')}
          </button>
          <button
            className="btn btn-primary"
            form="server-form"
            type="submit"
            disabled={
              busy ||
              !form.name.trim() ||
              !form.host.trim() ||
              (form.ownership === 'CUSTOMER' && !form.customer_id) ||
              (!server && form.auth_type === 'PASSWORD' && !form.password) ||
              (!server && form.auth_type === 'SSH_KEY' && !form.private_key)
            }
          >
            {busy ? t('servers.form.saving') : t('servers.form.save')}
          </button>
        </>
      }
    >
      <form id="server-form" onSubmit={submit} className="form-grid">
        <label className="full">
          <span>{t('servers.name')} *</span>
          <input
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            required
            maxLength={100}
          />
        </label>
        <label>
          <span>{t('servers.form.ownership')} *</span>
          <select
            value={form.ownership}
            onChange={(e) => update('ownership', e.target.value as 'SELF' | 'CUSTOMER')}
          >
            <option value="SELF">{t('servers.form.self')}</option>
            <option value="CUSTOMER">{t('servers.form.customer')}</option>
          </select>
        </label>
        <label>
          <span>{t('servers.form.mgmt')}</span>
          <select
            value={form.management_mode}
            onChange={(e) => update('management_mode', e.target.value as 'MANAGED' | 'EXTERNAL')}
          >
            <option value="MANAGED">{t('servers.form.managed')}</option>
            <option value="EXTERNAL">{t('servers.form.external')}</option>
          </select>
        </label>
        {form.ownership === 'CUSTOMER' && (
          <label className="full">
            <span>{t('common.company')} *</span>
            <select
              value={form.customer_id}
              onChange={(e) => update('customer_id', e.target.value)}
              required
            >
              <option value="">{t('sites.form.selectCustomer')}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span>{t('servers.host')} *</span>
          <input
            value={form.host}
            onChange={(e) => update('host', e.target.value)}
            required
            maxLength={255}
          />
        </label>
        <label>
          <span>{t('servers.port')}</span>
          <input
            type="number"
            value={form.ssh_port}
            onChange={(e) => update('ssh_port', Number(e.target.value))}
            min={1}
            max={65535}
          />
        </label>
        <label>
          <span>{t('servers.user')}</span>
          <input
            value={form.ssh_user}
            onChange={(e) => update('ssh_user', e.target.value)}
            maxLength={50}
          />
        </label>
        <label>
          <span>{t('servers.authMethod')}</span>
          <select
            value={form.auth_type}
            onChange={(e) => update('auth_type', e.target.value as 'PASSWORD' | 'SSH_KEY')}
          >
            <option value="PASSWORD">{t('servers.authPassword')}</option>
            <option value="SSH_KEY">{t('servers.authSshKey')}</option>
          </select>
        </label>
        {form.auth_type === 'PASSWORD' ? (
          <label className="full">
            <span>{server ? t('servers.passwordKeep') : `${t('servers.password')} *`}</span>
            <input
              type="password"
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              autoComplete="new-password"
              maxLength={4096}
              required={!server}
            />
          </label>
        ) : (
          <label className="full">
            <span>{server ? t('servers.sshKeyKeep') : `${t('servers.sshKey')} *`}</span>
            <textarea
              value={form.private_key}
              onChange={(e) => update('private_key', e.target.value)}
              rows={6}
              maxLength={16384}
              required={!server}
              placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----'}
            />
          </label>
        )}
        <p className="full muted small">
          {t('servers.credentialsHint')}
        </p>
      </form>
    </Modal>
  );
}
